import { Badge, Group, Progress, Stack, Text } from "@mantine/core";
import { guideStatusMeta, type GuideStatus } from "./guideStatus";

export interface GuideProgressProps {
  /** Steps done (own, company and automatically detected ones). */
  done: number;
  /** Steps the viewer can see. */
  total: number;
  /** The guide's status — drives the badge and the bar colour. */
  status: GuideStatus | string;
  /**
   * `"sm"` for a card in a list, `"md"` for the head of a guide.
   * @default "sm"
   */
  size?: "sm" | "md";
  /** Hide the status badge (e.g. when the surrounding UI already shows it). */
  hideBadge?: boolean;
}

/**
 * How far a member is through a guide: a status badge, a thin progress bar
 * and "2 of 5 steps". Presentational only — the numbers come from the guides
 * API, and the bar always shows them as they are.
 */
export function GuideProgress({ done, total, status, size = "sm", hideBadge = false }: GuideProgressProps) {
  const meta = guideStatusMeta(status);
  const value = total > 0 ? Math.round((Math.min(done, total) / total) * 100) : 0;
  return (
    <Stack gap={size === "md" ? 6 : 4}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text fz={size === "md" ? "sm" : "xs"} c="dimmed">
          {total === 0 ? "No steps yet" : `${Math.min(done, total)} of ${total} step${total === 1 ? "" : "s"}`}
        </Text>
        {!hideBadge && (
          <Badge size={size === "md" ? "sm" : "xs"} variant="light" color={meta.color}>
            {meta.label}
          </Badge>
        )}
      </Group>
      <Progress
        value={value}
        size={size === "md" ? "md" : "sm"}
        radius="xl"
        color={meta.color === "gray" ? "blue" : meta.color}
        aria-label={`${meta.label}: ${Math.min(done, total)} of ${total} steps`}
      />
    </Stack>
  );
}
